import React, { Component } from 'react';
import 'aframe';
import './controls/OrbitControls';
import 'core-js/features/symbol/iterator';
import './components/cubemap';
import 'aframe-html-shader';
import './components/aframeHelpers';
import { ScreenReaderOnly } from '../common/ADA';

import MainMediaControls from '../ViewWrapperContainer/ViewWrapper/MainMediaControls/MainMediaControls';
import TourLogoWrapper from '../ViewWrapperContainer/ViewWrapper/TourLogoWrapper/TourLogoWrapper';
import VRControls from './components/VRControls/VRControls';
import StopMediaHTML from './components/VRControls/StopMedia/StopMediaHTML';
import closeBtn from '../../images/close-btn@3x.png';
import {
  enterVRMode,
  toggleFullScreen,
  exitVRMode,
  modifyFooterClass,
  FADE_OUT_TIME,
  FADE_PAUSE_TIME,
  FADE_IN_TIME,
  setCamera,
} from './components/AFrameShared';
import PanoControls from '../Popup/PanoControls/PanoControls';
import HotspotWrapperContainer from '../Hotspot/HotspotWrapperContainer';
import { customGaEvent, GA_EVENTS } from '../../utils/ga';
import './AFramePanorama.scss';
import * as constants from "../../constants";

const defaultView = {
  position: { x: 0, y: 0, z: -0.2 },
  rotation: { x: -8.414080508010864, y: 0, z: 0 },
};

class AFramePanorama extends Component {
  constructor(props) {
    super(props);
    this.state = {
      src: props.src,
      newSrc: false,
      minZoom: 40,
      maxZoom: 100,
      flatPano: false,
      autoRotate: false,
      autoRotateDirection: 'counterclockwise',
      pauseAutoRotate: false,
      showPano: true,
      showHotspots: false,
      VRMode: false,
      VRStopMedia: false,
    };

    this.camera = null;
    this.scene = null;
  }

  componentDidMount() {
    this.initialize();
  }

  initialize() {
    this.cameraRig = document.getElementById('camera-rig');
    this.camera = document.getElementById('camera');
    this.scene = document.getElementById('scene');
    this.sky = document.getElementById('sky');
    this.camera.addEventListener('componentchanged', this.handlePanoRotate);
    this.scene.addEventListener('user-interaction-detected', this.handleUserInteraction);
    this.scene.addEventListener('hotspot-focus', this.handleUserInteraction);
    this.sky.addEventListener('animationcomplete__fadein', () => {
      this.setState({ showHotspots: true });
      this.sky.setAttribute('fullOpacity', true); // used for testing purposes
    });
    this.setInitialView();
    this.initializeHandleNewSrcFade();
    this.initializeHandleExitVR();
  }

  componentDidUpdate(prevProps, prevState) {
    const { src, initialView, activeStop } = this.props;
    const { showPano } = this.state;

    if (!prevState.showPano && showPano) {
      this.initialize();
    }
    // this.props.src !== undefined prevents setState memory leak if unmounting as in closing MediaModal when fade does not need to happen
    if (src !== undefined && prevProps.src !== src) {
      this.sky && this.sky.emit('fadeout');
      //wait for fade out, then swap for new src.
      this.setState({ showHotspots: false });

      setTimeout(() => {
        this.setInitialView();
        this.setState({
          src,
          newSrc: true,
        });
      }, FADE_OUT_TIME + 50);
    }

    if (prevState.VRMode && !this.state.VRMode) {
      this.camera.setAttribute('active', true);
    }

    if (!prevState.VRMode && this.state.VRMode) {
      this.camera.setAttribute('active', false);

      if (initialView.rotation && initialView.position) {
        this.setVRView(initialView.rotation);
      } else {
        this.setVRView(defaultView.rotation);
      }
    }

    if (prevProps.activeStop !== activeStop) {
      this.setPauseAutoRotate(false);
    }

    // Add interactive control instructions to canvas element so it is focused in Firefox
    const [canvasElement] = document.getElementsByClassName(
      'a-canvas a-grab-cursor'
    );
    // Check if element exists before setting attribute; only needed when exiting VR mode
    canvasElement && canvasElement.setAttribute('aria-hidden', 'true');
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  setInitialView = () => {
    const { initialView, ratio } = this.props;
    this.setFlatPanoView(ratio);
    if (initialView && initialView.position && initialView.rotation) {
      this.setPanoView(initialView);
    } else {
      this.setPanoView(defaultView);
    }
  };

  removeListeners = () => {
    this.camera.removeEventListener('componentchanged', this.handlePanoRotate);

    if (this.scene) {
      this.scene.removeEventListener('exit-vr', () => {
        console.log('exit-vr listener removed');
      });
      this.scene.removeEventListener('user-interaction-detected', this.handleUserInteraction);
      this.scene.removeEventListener('hotspot-focus', this.handleUserInteraction);
    }

    if (this.sky) {
      this.sky.removeEventListener('srcLoaded', () => {
        console.log('srcLoaded listener removed');
      });

      this.sky.removeEventListener('animationcomplete__fadein', () => {});
    }
  };

  initializeHandleNewSrcFade = () => {
    // NOT destructuring props to prevent cached values from being used in evt listener callback
    this.sky &&
      this.sky.addEventListener('srcLoaded', () => {
        this.setState({ newSrc: true });
        // reset zoom to initial value immediately after new source loads
        if (this.camera &&
            this.camera.components['orbit-controls'] &&
            this.camera.components['orbit-controls'].camera) {
          this.camera.components['orbit-controls'].setInitialZoom();
        }
        setTimeout(() => {
          if (this.sky) {
            this.sky.emit('fadein');
            setTimeout(() => {
              this.setState({
                newSrc: false,
              });
            }, FADE_IN_TIME);
          }
        }, FADE_PAUSE_TIME);
      });
  };

  initializeHandleExitVR = () => {
    this.scene.addEventListener('exit-vr', () => {
      this.setState({
        VRMode: false,
        showPano: false,
      });
      setTimeout(() => {
        this.setState({
          newSrc: true,
          showPano: true,
        });
      }, 0);
      this.setExitVRView();
      modifyFooterClass(false);
    });
  };

  handlePanoRotate = evt => {
    const { getPanoRotation, initialView, northView, viewOnly } = this.props;
    // Default values: initialView = {}, northView = "". This function needs to be called in two scenarios:
    // 1) Media Modal when setting default view and/or north view. Values will be default.
    // 2) Shared tour when initialView and/or northView have been set. Values will be unique.
    // viewOnly prop distinguishes b/w cases 1 and 2. Tracking the pano rotation value is an
    // expensive operation so this limits it significantly.

    if (viewOnly) {
      if (typeof getPanoRotation === 'function' && initialView && northView) {
        getPanoRotation(evt, this.camera);
      }
    } else if (typeof getPanoRotation === 'function') {
      getPanoRotation(evt, this.camera);
    }
  };

  // turn off rotate when user interacts with the scene
  handleUserInteraction = () => {
    this.setState({
      autoRotate: false,
    });
  };

  toggleVR = enter => {
    if (this.scene == null) {
      return;
    }
    const { tourData, history } = this.props;
    const { VRMode } = this.state;

    if (!VRMode && enter && this.scene.hasLoaded) {
      enterVRMode(tourData, history, this.scene);
      this.updateVRStates(true);
    } else {
      exitVRMode(this.scene, tourData);
      this.updateVRStates(false);
    }
  };

  updateVRStates = enterOrExit => {
    if (enterOrExit) {
      // entered VR
      this.setState({
        VRMode: true,
      });
    } else {
      // exited VR
      this.setState(
        {
          VRMode: false,
          VRStopMedia: false,
          showPano: false,
        },
        () => {
          this.setState({
            newSrc: true,
            showPano: true,
          });
        }
      );
    }
  };

  toggleStopMedia = () => {
    this.setState(state => ({ VRStopMedia: !state.VRStopMedia }));
  };

  setFlatPanoView = ratio => {
    if (ratio > 2.1) {
      this.setState({ maxZoom: 60, flatPano: true });
      setTimeout(() => this.changeZoom(0, 0), 0);
    } else {
      this.setState({ maxZoom: 100, flatPano: false });
    }
  };

  changeZoom = (deltaY, browserMultiplier) => {
    const { minZoom, maxZoom } = this.state;
    if (this.camera && this.camera.components.camera) {
      const { camera } = this.camera.components.camera;
      if (camera) {
        const fov = camera.fov + deltaY * browserMultiplier;
        // eslint-disable-next-line no-undef
        camera.fov = AFRAME.THREE.clamp(fov, minZoom, maxZoom);
        camera.updateProjectionMatrix();
      }
    }
  };

  toggleAutoRotate = () => {
    const { tourData } = this.props;
    const { autoRotate } = this.state;

    this.setState(
      prevState => ({ autoRotate: !prevState.autoRotate }),
      () => {
        // store auto-rotate state in sessionStorage when user clicks
        sessionStorage.setItem('autoRotate', JSON.stringify(this.state.autoRotate));

        // Only trigger GA event when user interacts with the button
        if (tourData) {
          customGaEvent({
            action: GA_EVENTS.AUTO_ROTATE,
            label: autoRotate ? 'Start' : 'Stop',
          });
        }
      }
    );
  };

  setPauseAutoRotate = isPaused => {
    this.setState({ pauseAutoRotate: isPaused });
  };

  getCameraRotationView = () => {
    const rotation = this.camera.getAttribute('rotation');
    const position = this.camera.getAttribute('position');
    return {
      rotation,
      position,
    };
  };

  setPanoView = initialView => {
    const {
      autoRotate: stateAutoRotate,
      autoRotateDirection: stateAutoRotateDirection,
    } = this.state;
    const {
      autoRotate: propsAutoRotate,
      autoRotateDirection: propsAutoRotateDirection,
    } = this.props;
    setCamera(this.camera, initialView, true);

    if (propsAutoRotate !== undefined && stateAutoRotate !== propsAutoRotate) {
      this.setState({ autoRotate: propsAutoRotate });
    }
    if (stateAutoRotateDirection !== propsAutoRotateDirection) {
      this.setState({ autoRotateDirection: propsAutoRotateDirection });
    }
  };

  setVRView = rotation => {
    this.cameraRig.setAttribute('rotation', { y: rotation.y });
    // eslint-disable-next-line
    AFRAME.utils.device.isMobile() &&
      this.cameraRig.setAttribute('position', { x: 0, y: -1.6, z: 0 });
  };

  setExitVRView = () => {
    this.cameraRig.setAttribute('rotation', { y: 0 });
    this.cameraRig.setAttribute('position', { x: 0, y: 0, z: 0 });
  };

  c3dGalleryClick = () => {
    customGaEvent({
      action: GA_EVENTS.GALLERY_CLICK,
      label: 'clicked',
    });
  };

  handleExitFullscreen = () => {
    // should handle chrome and ff
    if (typeof document.exitFullscreen === 'function') {
      document.exitFullscreen();
    }

    // new webkit
    if (typeof document.webkitExitFullscreen === 'function') {
      document.webkitExitFullscreen();
    }

    // old webkit
    if (typeof document.webkitCancelFullScreen === 'function') {
      document.webkitCancelFullScreen();
    }
  };

  render() {
    const {
      autoRotate,
      autoRotateDirection,
      pauseAutoRotate,
      flatPano,
      newSrc,
      showHotspots,
      showPano,
      src,
      VRMode,
      VRStopMedia,
    } = this.state;

    const {
      activeStop,
      changeStop,
      checkLogoURL,
      closeShareModal,
      cube,
      currentStop,
      history,
      initialView,
      miniMapView,
      panoAltText,
      panoHasKeyboardControl,
      primaryColor,
      publicTour,
      resetInitialView,
      setYPosition,
      showTransitionLogo,
      showVRMode,
      sky,
      stops,
      tier,
      tourData,
      tourPublished,
      viewOnly,
      isMainMediaPlaying,
      toggleMainMediaStatus,
      isMobile,
      showPanoControls,
      togglePanoControls,
      accountPermissions = {},
    } = this.props;
    const hasHotspots =
      currentStop &&
      currentStop.hotspots &&
      Object.keys(currentStop.hotspots).length > 0;

    let panoViewSource;

    if (sky) {
      panoViewSource = <a-sky src={`${src}`} radius="100" id="sky" aria-label={panoAltText} role="img" />;
    }
    if (cube && src) {
      const splitSrc = src.split('/');
      const cubeSrc = `${splitSrc[0]}/${splitSrc[1]}/${splitSrc[2]}/${splitSrc[3]}/${splitSrc[4]}/${splitSrc[4]}.tiles/`;

      panoViewSource = (
        <a-entity
          aria-label={panoAltText}
          role="img"
          fullOpacity={false}
          cubemap={`
            folder: ${cubeSrc};
            urls: pano_r, pano_l, pano_u, pano_d, pano_f, pano_b;
            edgeLength: 5000;
            transparent: true;
            opacity: ${newSrc ? 0 : 1},
          `}
          animation__fadeout={`
            property: cubemap.opacity;
            startEvents: fadeout;
            to: 0;
            dur: ${FADE_OUT_TIME}
          `}
          animation__fadein={`
            property: cubemap.opacity;
            startEvents: fadein;
            to: 1;
            dur: ${FADE_IN_TIME}
          `}
          id="sky"
        ></a-entity>
      );
    }

    const shouldShowHotspots =
      hasHotspots && showHotspots && accountPermissions.hotspots;
    const isMini = miniMapView === constants.PANOS;

    return (
      <div
        id="pano360"
        className={!showTransitionLogo ? 'hide-logo' : ''}
        onMouseDown={resetInitialView}
      >
        {showPano && (
          <React.Fragment>
            { tourData && !isMini && (
              <TourLogoWrapper
                tourId={tourData && tourData.id}
                tourName={tourData && tourData.name}
                currentTour={tourData}
                tier={tier}
                checkLogoURL={checkLogoURL}
                publicTour={publicTour}
              />
            )}
            <a-scene
              id="scene"
              cursor="rayOrigin: mouse; fuseTimeout: 0;"
              raycaster="objects: .clickable;"
              vr-mode-ui={`enabled: true`}
              device-orientation-permission-ui="enabled: false"
              loading-screen="enabled: false"
              role="region"
              embedded
              aria-label={`${isMini ? 'Mini interactive 360-degree panorama' : 'Interactive 360-degree panorama'}`}
            >
              <ScreenReaderOnly
                id="pano-alt-text"
                tabIndex={0}
                role="img"
                aria-label={panoAltText}
              >
                {panoAltText}
              </ScreenReaderOnly>
              {miniMapView === 'map' && (
                <React.Fragment>
                  {!isMobile && showPanoControls && (
                    <PanoControls
                      mediaType="panorama"
                      tourData={tourData}
                      togglePanoControls={togglePanoControls}
                      showPanoControls={showPanoControls}
                    />
                  )}
                  <MainMediaControls
                    closeShareModal={closeShareModal}
                    hasSingleButton={tourData.buttons.length}
                    is360Pano
                    miniMapView={miniMapView}
                    panoRotating={autoRotate}
                    primaryColor={primaryColor}
                    setYPosition={setYPosition}
                    showVRMode={showVRMode}
                    stopIds={tourData?.stopIds}
                    stops={tourData && stops}
                    tier={tier}
                    toggleAutoRotate={this.toggleAutoRotate}
                    autoRotateDirection={autoRotateDirection}
                    toggleFullScreen={() => toggleFullScreen(tourData)}
                    toggleVR={this.toggleVR}
                    tourPublished={tourPublished}
                    viewOnly={viewOnly}
                    VRMode={VRMode}
                    currentStopId={currentStop.id}
                  />
                </React.Fragment>
              )}
              <a-assets>
                <img
                  src={closeBtn}
                  id="exit-fullscreen-btn"
                  alt=""
                  crossOrigin="anonymous"
                />
              </a-assets>
              {panoViewSource}
              {shouldShowHotspots && (
                <HotspotWrapperContainer
                  hotspotIcons={tourData?.styles?.hotspotIcons}
                  hotspots={currentStop.hotspots}
                  setPauseAutoRotate={this.setPauseAutoRotate}
                  history={history}
                  changeStop={changeStop}
                  tourId={tourData?.id}
                  tourName={tourData?.name}
                  vrMode={VRMode}
                />
              )}
              <a-entity id="camera-rig">
                <a-camera
                  id="camera"
                  position="0 0 0.2"
                  user-height="1.5"
                  wasd-controls-enabled="false"
                  look-controls-enabled="false"
                  orbit-controls={`
                    enableKeys:${panoHasKeyboardControl};
                    keyRotateSpeed: 1;
                    autoRotate: ${autoRotate && !pauseAutoRotate};
                    autoRotateSpeed: ${
                      autoRotateDirection !== 'clockwise' ? -0.05 : 0.05
                    };
                    target: #cameratarget;
                    enableDamping: true;
                    enableZoom: true;
                    dampingFactor: 0.11;
                    rotateSpeed: -0.14;
                    touchRotateSpeed: -0.05;
                    minZoom: 0.75;
                    maxZoom: 2;
                    initialZoom: 0.75;
                    minDistance: 0.15;
                    maxDistance: 0.15;
                    maxPolarAngle: ${flatPano ? 1.57 : 3.14};
                    minPolarAngle: ${flatPano ? 1.57 : 0};
                    autoVRLookCam: true;
                  `}
                >
                </a-camera>
                {VRMode && (
                  <a-camera
                    id="vr-camera"
                    position="0 0 0"
                    wasd-controls-enabled="false"
                    look-controls-enabeld="true"
                    disable-culling // fixes iOS stacked box VR issue [TB-1971]
                  >
                    <a-entity
                      geometry="primitive: box;"
                      material="shader: html; target: #htmlElement;"
                    />
                    <a-entity
                      position="0 0 -3"
                      geometry="primitive: ring; thetaLength: 360; radiusOuter: 0.06; radiusInner: 0.04; face: front;"
                      material="color: #009c6b; shader: flat; transparent: true;"
                    />
                    <a-entity
                      position="0 0 -3"
                      geometry="primitive: ring; thetaLength: 360; radiusOuter: 0.06; radiusInner: 0.04; face: front;"
                      material="color: #fff; shader: flat; transparent: true;"
                      raycaster="interval: 100; objects: .clickable; recursive: true;"
                      cursor="fuse: true; fuseTimeout: 500;"
                      animation="property: geometry.thetaLength; to: 0; startEvents: fusing; dur: 500;"
                      animation__leave="property: geometry.thetaLength; to: 360; startEvents: mouseleave; dur: 500;"
                    ></a-entity>
                  </a-camera>
                )}
              </a-entity>
              {VRMode && (
                <VRControls
                  toggleVR={this.toggleVR}
                  changeStop={changeStop}
                  activeStop={activeStop}
                  currentStop={currentStop}
                  toggleStopMedia={this.toggleStopMedia}
                  initialView={initialView}
                  isMainMediaPlaying={isMainMediaPlaying}
                  toggleMainMediaStatus={toggleMainMediaStatus}
                />
              )}
              <a-entity id="cameratarget" scale="1 1 1" position="0 0 0" />
            </a-scene>
          </React.Fragment>
        )}
        {VRMode && VRStopMedia && (
          <StopMediaHTML
            name={currentStop.name}
            description={currentStop.description}
            toggleStopMedia={this.toggleStopMedia}
          />
        )}
      </div>
    );
  }
}

export default AFramePanorama;
